/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError, mergeMap } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

const requestManualDowntimePeriodsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedManualDowntimePeriodsSuccess.create(response);
};

const requestManualDowntimePeriodsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedManualDowntimePeriodsError.create(error);
};

export const requestManualDowntimePeriodsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestManualDowntimePeriods.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/downtime/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => requestManualDowntimePeriodsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestManualDowntimePeriodsError(error))
              ),
          ),
        ),
    ),
  );

const addManualDowntimePeriodSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.AddedManualDowntimePeriodSuccess.create(response);
};

const addManualDowntimePeriodError = (error: AjaxError): any => {
  return ActionCreators.AddedManualDowntimePeriodError.create(error);
};

export const addManualDowntimePeriodEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.AddManualDowntimePeriod.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/downtime', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => addManualDowntimePeriodSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(addManualDowntimePeriodError(error))
              ),
          ),
        ),
    ),
  );

const deleteManualDowntimePeriodSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.DeletedManualDowntimePeriodSuccess.create(response);
};
const removeManualDowntimePeriodFromStoreById = (id: string): any => {
  return ActionCreators.RemoveManualDowntimePeriodFromStoreById.create(id);
};

const deleteManualDowntimePeriodError = (error: AjaxError): any => {
  return ActionCreators.DeletedManualDowntimePeriodError.create(error);
};

export const deleteManualDowntimePeriodEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteManualDowntimePeriod.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.delete(`/downtime/${$action.payload}`).pipe(
          mergeMap((response: AjaxResponse<any>): Action[] => [
            deleteManualDowntimePeriodSuccess(response),
            removeManualDowntimePeriodFromStoreById($action.payload),
          ]),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(deleteManualDowntimePeriodError(error))
              ),
          ),
        ),
    ),
  );
